// Rent-receipt endpoint functions.
//
// Accept camelCase view-model inputs, return camelCase view models. Throw
// AppError (the httpClient interceptor normalises) — never wrap in Result<T>.

import { adminApi } from '@core/api/httpClient'
import type { PaginatedResponse } from '@shared/types/pagination'
import { RENT_RECEIPTS_ENDPOINT_PATHS } from './rentReceiptsEndpointPaths'
import type {
  CreatedRentReceiptDto,
  PreviewRentReceiptDto,
  ReconciliationResultDto,
  RentReceiptCountsDto,
  RentReceiptDetailDto,
  RentReceiptsListDto,
  RentReceiptApprovalPermissionDto,
} from './rentReceiptsContracts'
import {
  parsePreviewRentReceipt,
  parseReconciliationResult,
  parseRentReceiptCounts,
  parseRentReceiptDetail,
  parseRentReceiptsList,
  parseRentReceiptApprovalPermission,
} from './rentReceiptsParser'
import type {
  CreateRentReceiptInput,
  PreviewRentReceipt,
  PreviewRentReceiptInput,
  ReconciliationResult,
  RentReceipt,
  RentReceiptCounts,
  RentReceiptDetail,
  RentReceiptFilters,
  UpdateRentReceiptInput,
  RentReceiptApprovalPermission,
} from '../types/rentReceiptTypes'

/**
 * Every admin-service response is wrapped by its global transform interceptor
 * as `{ success, statusCode, message, data }`. Strip that one level here so no
 * parser ever sees the transport envelope — including list responses, whose
 * payload is itself `{ data, meta }`.
 */
function unwrap<T>(payload: T | { data: T }): T {
  if (payload && typeof payload === 'object' && 'data' in payload) {
    return (payload as { data: T }).data
  }
  return payload as T
}

/**
 * Status is an array — several queue tabs span more than one status — and axios
 * would serialise it as `status[]=`. A comma-joined value is what the backend's
 * transform expects.
 */
function toQueryParams(filters: RentReceiptFilters): Record<string, string> {
  const params: Record<string, string> = {}

  for (const [key, value] of Object.entries(filters)) {
    if (value === undefined || value === null || value === '') continue
    if (Array.isArray(value)) {
      if (value.length > 0) params[key] = value.join(',')
      continue
    }
    params[key] = String(value)
  }

  return params
}

// ─── Reads ──────────────────────────────────────────────────────────────────

export async function getRentReceipts(
  filters: RentReceiptFilters,
): Promise<PaginatedResponse<RentReceipt>> {
  const { data } = await adminApi.get<RentReceiptsListDto | { data: RentReceiptsListDto }>(
    RENT_RECEIPTS_ENDPOINT_PATHS.LIST,
    { params: toQueryParams(filters) },
  )
  return parseRentReceiptsList(unwrap(data))
}

export async function getRentReceiptCounts(): Promise<RentReceiptCounts> {
  const { data } = await adminApi.get<RentReceiptCountsDto | { data: RentReceiptCountsDto }>(
    RENT_RECEIPTS_ENDPOINT_PATHS.STATUS_COUNTS,
  )
  return parseRentReceiptCounts(unwrap(data))
}

export async function getRentReceipt(receiptId: string): Promise<RentReceiptDetail> {
  const { data } = await adminApi.get<RentReceiptDetailDto | { data: RentReceiptDetailDto }>(
    RENT_RECEIPTS_ENDPOINT_PATHS.DETAIL(receiptId),
  )
  return parseRentReceiptDetail(unwrap(data))
}

/**
 * Whether the signed-in admin may approve this receipt, and why not.
 *
 * Read separately from the receipt itself for the same reason as the
 * distribution-run permission queries — the answer depends on WHO is asking.
 */
export async function getRentReceiptApprovalPermission(
  receiptId: string,
): Promise<RentReceiptApprovalPermission> {
  const { data } = await adminApi.get<
    RentReceiptApprovalPermissionDto | { data: RentReceiptApprovalPermissionDto }
  >(RENT_RECEIPTS_ENDPOINT_PATHS.APPROVAL_PERMISSION(receiptId))
  return parseRentReceiptApprovalPermission(unwrap(data))
}

// ─── Writes ─────────────────────────────────────────────────────────────────

export async function createRentReceipt(input: CreateRentReceiptInput): Promise<string> {
  const { data } = await adminApi.post<CreatedRentReceiptDto | { data: CreatedRentReceiptDto }>(
    RENT_RECEIPTS_ENDPOINT_PATHS.CREATE,
    input,
  )
  return unwrap(data).id
}

/** The Layer-1 deduction lines a receipt would get if recorded right now. Writes nothing. */
export async function previewRentReceipt(
  input: PreviewRentReceiptInput,
): Promise<PreviewRentReceipt> {
  const { data } = await adminApi.post<PreviewRentReceiptDto | { data: PreviewRentReceiptDto }>(
    RENT_RECEIPTS_ENDPOINT_PATHS.PREVIEW,
    input,
  )
  return parsePreviewRentReceipt(unwrap(data))
}

export async function updateRentReceipt(
  receiptId: string,
  input: UpdateRentReceiptInput,
): Promise<void> {
  await adminApi.patch(RENT_RECEIPTS_ENDPOINT_PATHS.UPDATE(receiptId), input)
}

export async function reconcileRentReceipt(receiptId: string): Promise<ReconciliationResult> {
  const { data } = await adminApi.post<ReconciliationResultDto | { data: ReconciliationResultDto }>(
    RENT_RECEIPTS_ENDPOINT_PATHS.RECONCILE(receiptId),
  )
  return parseReconciliationResult(unwrap(data))
}

export async function acceptRentReceiptVariance(receiptId: string, reason: string): Promise<void> {
  await adminApi.post(RENT_RECEIPTS_ENDPOINT_PATHS.ACCEPT_VARIANCE(receiptId), { reason })
}

export async function disputeRentReceipt(receiptId: string, reason: string): Promise<void> {
  await adminApi.post(RENT_RECEIPTS_ENDPOINT_PATHS.DISPUTE(receiptId), { reason })
}

export async function acknowledgeRentReceiptThresholds(receiptId: string): Promise<void> {
  await adminApi.post(RENT_RECEIPTS_ENDPOINT_PATHS.ACKNOWLEDGE_THRESHOLDS(receiptId))
}

export async function submitRentReceiptForApproval(receiptId: string): Promise<void> {
  await adminApi.post(RENT_RECEIPTS_ENDPOINT_PATHS.SUBMIT_FOR_APPROVAL(receiptId))
}

export async function approveRentReceipt(receiptId: string): Promise<void> {
  await adminApi.post(RENT_RECEIPTS_ENDPOINT_PATHS.APPROVE(receiptId))
}

export async function rejectRentReceipt(receiptId: string, reason: string): Promise<void> {
  await adminApi.post(RENT_RECEIPTS_ENDPOINT_PATHS.REJECT(receiptId), { reason })
}

export async function correctRentReceipt(
  receiptId: string,
  input: CreateRentReceiptInput,
): Promise<string> {
  const { data } = await adminApi.post<CreatedRentReceiptDto | { data: CreatedRentReceiptDto }>(
    RENT_RECEIPTS_ENDPOINT_PATHS.CORRECT(receiptId),
    input,
  )
  return unwrap(data).id
}
